import React, { useState, useMemo } from "react";
import "./dashboard.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Plus,
  Lock,
  BarChart3,
  MessageCircle,
  Phone,
  Mail,
  TrendingUp,
  DollarSign,
  Building2,
  Coins,
  Shield,
  FileText,
} from "lucide-react";

const RADIAN = Math.PI / 180;

/* Helpers: color luminance */
const hexToRgb = (hex) => {
  const cleaned = hex.replace("#", "");
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  const bigint = parseInt(full, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};

const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const srgb = [r / 255, g / 255, b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
};

const MIN_PERCENT = 3; // minimum visible percent for any slice (tweakable)

const Dashboard = () => {
  const [performancePeriod, setPerformancePeriod] = useState("1M");

  // --- Static data memoized so identities don't change across renders ---
  const allocationData = useMemo(
    () => [
      { name: "Equity", value: 45, color: "#1E88E5" },
      { name: "Mutual Funds", value: 25, color: "#43A047" },
      { name: "Bonds", value: 15, color: "#8E24AA" },
      { name: "Gold", value: 10, color: "#FBC02D" },
      { name: "Cash", value: 5, color: "#616161" },
    ],
    []
  );

  const performanceData = useMemo(
    () => [
      { date: "Jan 1", value: 200000 },
      { date: "Jan 5", value: 205000 },
      { date: "Jan 10", value: 215000 },
      { date: "Jan 15", value: 218000 },
      { date: "Jan 20", value: 220000 },
      { date: "Jan 25", value: 225000 },
      { date: "Jan 30", value: 230000 },
    ],
    []
  );

  const holdings = useMemo(
    () => [
      {
        id: 1,
        name: "Tata Consultancy",
        type: "Technology",
        value: "₹45,680",
        change: "+5.2%",
        positive: true,
        logo: "TCS",
        bgColor: "#1E88E5",
      },
      {
        id: 2,
        name: "HDFC Bank",
        type: "Banking",
        value: "₹38,420",
        change: "+3.8%",
        positive: true,
        logo: "HDFC",
        bgColor: "#ef4444",
      },
      {
        id: 3,
        name: "ITC Limited",
        type: "FMCG",
        value: "₹32,150",
        change: "-1.2%",
        positive: false,
        logo: "ITC",
        bgColor: "#10b981",
      },
      {
        id: 4,
        name: "Infosys",
        type: "Technology",
        value: "₹28,950",
        change: "+4.1%",
        positive: true,
        logo: "NFY",
        bgColor: "#8b5cf6",
      },
    ],
    []
  );

  const transactions = useMemo(
    () => [
      {
        id: 1,
        type: "buy",
        name: "Bought HDFC Bank",
        date: "Today, 2:30 PM",
        amount: "₹5,000",
        icon: "↓",
        iconColor: "#10b981",
      },
      {
        id: 2,
        type: "sip",
        name: "SIP - Axis Bluechip",
        date: "Yesterday, 10:00 AM",
        amount: "₹3,000",
        icon: "≡",
        iconColor: "#1E88E5",
      },
      {
        id: 3,
        type: "sell",
        name: "Sold Reliance",
        date: "Jan 10, 3:45 PM",
        amount: "₹8,500",
        icon: "↑",
        iconColor: "#ef4444",
      },
      {
        id: 4,
        type: "dividend",
        name: "Dividend Received",
        date: "Jan 8, 11:20 AM",
        amount: "+₹1,250",
        icon: "+",
        iconColor: "#8b5cf6",
      },
    ],
    []
  );

  const quickActions = useMemo(
    () => [
      {
        id: 1,
        title: "Invest Now",
        icon: Plus,
        bgColor: "#dbeafe",
        iconColor: "#1E88E5",
      },
      {
        id: 2,
        title: "Start SIP",
        icon: Lock,
        bgColor: "#dcfce7",
        iconColor: "#43A047",
      },
      {
        id: 3,
        title: "View Report",
        icon: BarChart3,
        bgColor: "#f3e8ff",
        iconColor: "#8E24AA",
      },
      {
        id: 4,
        title: "Get Support",
        icon: MessageCircle,
        bgColor: "#fed7aa",
        iconColor: "#f97316",
      },
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        id: 1,
        name: "Stocks",
        icon: TrendingUp,
        bgColor: "#dbeafe",
        iconColor: "#1E88E5",
      },
      {
        id: 2,
        name: "Mutual Funds",
        icon: DollarSign,
        bgColor: "#dcfce7",
        iconColor: "#43A047",
      },
      {
        id: 3,
        name: "IPO",
        icon: Building2,
        bgColor: "#f3e8ff",
        iconColor: "#8E24AA",
      },
      {
        id: 4,
        name: "Gold",
        icon: Coins,
        bgColor: "#fef3c7",
        iconColor: "#FBC02D",
      },
      {
        id: 5,
        name: "Insurance",
        icon: Shield,
        bgColor: "#dcfce7",
        iconColor: "#43A047",
      },
      {
        id: 6,
        name: "Loans",
        icon: FileText,
        bgColor: "#fed7aa",
        iconColor: "#f97316",
      },
    ],
    []
  );

  const learningItems = useMemo(
    () => [
      {
        id: 1,
        title: "Stock Market Basics",
        description: "Learn how to read financial statements",
        duration: "12h",
        number: "1",
        bgColor: "#1E88E5",
      },
      {
        id: 2,
        title: "Technical Analysis",
        description: "Master candlestick patterns",
        duration: "8h",
        number: "2",
        bgColor: "#43A047",
      },
      {
        id: 3,
        title: "Risk Management",
        description: "Protect your investments",
        duration: "15h",
        number: "3",
        bgColor: "#8E24AA",
      },
    ],
    []
  );

  const events = useMemo(
    () => [
      {
        id: 1,
        title: "Webinar: IPO Investing",
        date: "Feb 15, 2024 | 6:00 PM",
        fee: "₹0",
        number: "1",
        bgColor: "#1E88E5",
      },
      {
        id: 2,
        title: "Live Trading Session",
        date: "Mar 3, 2024 | 8:00 PM",
        fee: "₹0",
        number: "2",
        bgColor: "#43A047",
      },
    ],
    []
  );

  // --- Build visualData from allocationData (guarantee minimum visible arc) ---
  const visualData = useMemo(() => {
    // copy and attach orig/display
    const orig = allocationData.map((d) => ({ ...d, orig: d.value }));

    // initial visual values
    let working = orig.map((d) => ({
      name: d.name,
      color: d.color,
      orig: d.orig,
      visual: Math.max(d.orig, MIN_PERCENT),
      display: d.orig, // show this true value as label
    }));

    const sumVisual = (arr) => arr.reduce((s, it) => s + it.visual, 0);
    let total = sumVisual(working);

    if (total > 100) {
      let surplus = total - 100;
      let reducibles = working.filter((w) => w.orig > MIN_PERCENT);
      if (reducibles.length === 0) {
        const scale = 100 / total;
        working = working.map((w) => ({
          ...w,
          visual: +(w.visual * scale).toFixed(2),
        }));
        // rounding fix
        let roundedTotal = sumVisual(working);
        let diff = Math.round((100 - roundedTotal) * 100) / 100;
        if (Math.abs(diff) >= 0.01) {
          let maxIdx = 0;
          for (let i = 1; i < working.length; i++) {
            if (working[i].visual > working[maxIdx].visual) maxIdx = i;
          }
          working[maxIdx].visual = +(working[maxIdx].visual + diff).toFixed(2);
        }
        return working;
      }

      let totalReducible = reducibles.reduce(
        (s, r) => s + (r.visual - MIN_PERCENT),
        0
      );

      working = working.map((w) => {
        if (w.orig <= MIN_PERCENT) return { ...w };
        const reducible = w.visual - MIN_PERCENT;
        const share =
          totalReducible === 0 ? 0 : (reducible / totalReducible) * surplus;
        let newVisual = w.visual - share;
        if (newVisual < MIN_PERCENT) newVisual = MIN_PERCENT;
        return { ...w, visual: +newVisual.toFixed(2) };
      });

      // iterative remainder fix
      let newTotal = sumVisual(working);
      let remainder = Math.round((newTotal - 100) * 100) / 100;
      let safety = 0;
      while (remainder > 0.0001 && safety < 20) {
        let idx = -1;
        let maxVal = -Infinity;
        for (let i = 0; i < working.length; i++) {
          const canReduce = working[i].visual - MIN_PERCENT;
          if (canReduce > 0.001 && working[i].visual > maxVal) {
            maxVal = working[i].visual;
            idx = i;
          }
        }
        if (idx === -1) break;
        const delta = Math.min(
          remainder,
          +(working[idx].visual - MIN_PERCENT).toFixed(2)
        );
        working[idx].visual = +(working[idx].visual - delta).toFixed(2);
        newTotal = sumVisual(working);
        remainder = Math.round((newTotal - 100) * 100) / 100;
        safety++;
      }

      // final tiny rounding correction
      newTotal = sumVisual(working);
      const diff = Math.round((100 - newTotal) * 100) / 100;
      if (Math.abs(diff) >= 0.01) {
        let maxIdx = 0;
        for (let i = 1; i < working.length; i++) {
          if (working[i].visual > working[maxIdx].visual) maxIdx = i;
        }
        working[maxIdx].visual = +(working[maxIdx].visual + diff).toFixed(2);
      }
    } else {
      // round and small correction so sum ~ 100
      working = working.map((w) => ({ ...w, visual: +w.visual.toFixed(2) }));
      let newTotal = sumVisual(working);
      const diff = Math.round((100 - newTotal) * 100) / 100;
      if (Math.abs(diff) >= 0.01) {
        let maxIdx = 0;
        for (let i = 1; i < working.length; i++) {
          if (working[i].visual > working[maxIdx].visual) maxIdx = i;
        }
        working[maxIdx].visual = +(working[maxIdx].visual + diff).toFixed(2);
      }
    }

    return working;
  }, [allocationData]);

  // customized label: always show original (display) value, put inside donut
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    index,
    payload,
  }) => {
    // payload is from visualData entry
    const entry = payload;
    const displayValue = entry.display;
    const useDarkText = getLuminance(entry.color) > 0.6;
    const isVerySmall = displayValue <= 5;
    const radius =
      innerRadius + (outerRadius - innerRadius) * (isVerySmall ? 0.45 : 0.6);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const fontSize = isVerySmall ? 10 : 13;
    const fill = useDarkText ? "#111" : "#fff";

    return (
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight={700}
        fontSize={fontSize}
        fill={fill}
        style={{ pointerEvents: "none" }}
      >
        {`${displayValue}%`}
      </text>
    );
  };

  return (
    <div className="wc-dashboard-wrapper">
      {/* Row 1: Performance + Allocation */}
      <div className="wc-dashboard-row-1">
        <div className="wc-dashboard-performance-card wc-dashboard-card">
          <div className="wc-dashboard-performance-header">
            <h3>Portfolio Performance</h3>
            <div className="wc-dashboard-period-selector">
              {["1M", "3M", "1Y"].map((p) => (
                <button
                  key={p}
                  className={`wc-dashboard-period-btn ${
                    performancePeriod === p ? "active" : ""
                  }`}
                  onClick={() => setPerformancePeriod(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={performanceData}>
              <defs>
                <linearGradient id="wc_colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E88E5" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#1E88E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
              />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: "12px" }}
                tickFormatter={(v) => `${v / 1000}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#1E88E5"
                strokeWidth={3}
                dot={false}
                fill="url(#wc_colorValue)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="wc-dashboard-allocation-card wc-dashboard-card">
          <h3>Asset Allocation</h3>

          <div className="wc-dashboard-allocation-content">
            <div className="wc-dashboard-allocation-legend-left">
              {allocationData.map((item) => (
                <div
                  key={item.name}
                  className="wc-dashboard-legend-item-left"
                  tabIndex={0}
                >
                  <span
                    className="wc-dashboard-legend-dot"
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="wc-dashboard-legend-label">{item.name}</div>
                </div>
              ))}
            </div>

            <div className="wc-dashboard-allocation-pie-wrap" aria-hidden>
              <ResponsiveContainer width={320} height={320}>
                <PieChart>
                  <Pie
                    data={visualData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={140}
                    paddingAngle={1}
                    dataKey="visual"
                    label={renderCustomizedLabel}
                    labelLine={false}
                  >
                    {visualData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Holdings + Transactions */}
      <div className="wc-dashboard-row-2">
        <div className="wc-dashboard-holdings-card wc-dashboard-card">
          <h3>Top Holdings</h3>
          <div className="wc-dashboard-holdings-list">
            {holdings.map((h) => (
              <div
                key={h.id}
                className="wc-dashboard-holding-item"
                tabIndex={0}
              >
                <div className="wc-dashboard-holding-left">
                  <div
                    className="wc-dashboard-holding-logo"
                    style={{ backgroundColor: h.bgColor }}
                  >
                    {h.logo}
                  </div>
                  <div className="wc-dashboard-holding-info">
                    <div className="wc-dashboard-holding-name">{h.name}</div>
                    <div className="wc-dashboard-holding-type">{h.type}</div>
                  </div>
                </div>
                <div className="wc-dashboard-holding-right">
                  <div className="wc-dashboard-holding-value">{h.value}</div>
                  <div
                    className={`wc-dashboard-holding-change ${
                      h.positive ? "positive" : "negative"
                    }`}
                  >
                    {h.change}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="wc-dashboard-transactions-card wc-dashboard-card">
          <div className="wc-dashboard-transactions-header">
            <h3>Recent Transactions</h3>
            <a href="#" className="wc-dashboard-view-all">
              View All
            </a>
          </div>

          <div className="wc-dashboard-transactions-list">
            {transactions.map((t) => (
              <div
                key={t.id}
                className="wc-dashboard-transaction-item"
                tabIndex={0}
              >
                <div className="wc-dashboard-transaction-left">
                  <div
                    className="wc-dashboard-transaction-icon"
                    style={{ backgroundColor: `${t.iconColor}20` }}
                  >
                    <span style={{ color: t.iconColor }}>{t.icon}</span>
                  </div>
                  <div className="wc-dashboard-transaction-info">
                    <div className="wc-dashboard-transaction-name">
                      {t.name}
                    </div>
                    <div className="wc-dashboard-transaction-date">
                      {t.date}
                    </div>
                  </div>
                </div>
                <div className="wc-dashboard-transaction-amount">
                  {t.amount}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="wc-dashboard-quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="wc-dashboard-quick-actions-grid">
          {quickActions.map((action) => {
            const IconComponent = action.icon;
            return (
              <div
                key={action.id}
                className="wc-dashboard-quick-action-item wc-dashboard-card"
                tabIndex={0}
              >
                <div
                  className="wc-dashboard-quick-action-icon"
                  style={{ backgroundColor: action.bgColor }}
                >
                  <IconComponent size={28} color={action.iconColor} />
                </div>
                <div className="wc-dashboard-quick-action-title">
                  {action.title}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Account Manager & Connect */}
      <div className="wc-dashboard-row-4">
        <div className="wc-dashboard-account-manager-card wc-dashboard-card">
          <h3>Account Manager</h3>
          <div className="wc-dashboard-manager-info">
            <div className="wc-dashboard-manager-avatar">AM</div>
            <div className="wc-dashboard-manager-details">
              <div className="wc-dashboard-manager-name">Rajesh Kumar</div>
              <div className="wc-dashboard-manager-role">
                Senior Account Manager
              </div>
            </div>
            <div className="wc-dashboard-manager-contact">
              <div className="wc-dashboard-manager-phone">+91 98765 43210</div>
              <div className="wc-dashboard-manager-status">Available</div>
            </div>
          </div>
        </div>

        <div className="wc-dashboard-connect-card wc-dashboard-card">
          <h3>Connect with Us</h3>
          <div className="wc-dashboard-connect-list">
            <div className="wc-dashboard-connect-item" tabIndex={0}>
              <div
                className="wc-dashboard-connect-icon"
                style={{ backgroundColor: "#dbeafe" }}
              >
                <Phone size={20} color="#1E88E5" />
              </div>
              <div className="wc-dashboard-connect-info">
                <div className="wc-dashboard-connect-label">Call Us</div>
                <div className="wc-dashboard-connect-value">
                  +91 1234 567890
                </div>
              </div>
            </div>
            <div className="wc-dashboard-connect-item" tabIndex={0}>
              <div
                className="wc-dashboard-connect-icon"
                style={{ backgroundColor: "#dcfce7" }}
              >
                <Mail size={20} color="#43A047" />
              </div>
              <div className="wc-dashboard-connect-info">
                <div className="wc-dashboard-connect-label">Email</div>
                <div className="wc-dashboard-connect-value">
                  support@financehub.com
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services */}
      <div className="wc-dashboard-services-section">
        <div className="wc-dashboard-services-header">
          <h3>Our Services</h3>
          <a href="#" className="wc-dashboard-view-all">
            View All
          </a>
        </div>
        <div className="wc-dashboard-services-grid">
          {services.map((s) => {
            const IconComponent = s.icon;
            return (
              <div
                key={s.id}
                className="wc-dashboard-service-item wc-dashboard-card"
                tabIndex={0}
              >
                <div
                  className="wc-dashboard-service-icon"
                  style={{ backgroundColor: s.bgColor }}
                >
                  <IconComponent size={32} color={s.iconColor} />
                </div>
                <div className="wc-dashboard-service-name">{s.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Learning & Events */}
      <div className="wc-dashboard-row-6">
        <div className="wc-dashboard-learning-card wc-dashboard-card">
          <h3>Learning Center</h3>
          <div className="wc-dashboard-learning-list">
            {learningItems.map((item) => (
              <div
                key={item.id}
                className="wc-dashboard-learning-item"
                tabIndex={0}
              >
                <div
                  className="wc-dashboard-learning-number"
                  style={{ backgroundColor: item.bgColor }}
                >
                  {item.number}
                </div>
                <div className="wc-dashboard-learning-content">
                  <div className="wc-dashboard-learning-title">
                    {item.title}
                  </div>
                  <div className="wc-dashboard-learning-desc">
                    {item.description}
                  </div>
                </div>
                <div className="wc-dashboard-learning-duration">
                  {item.duration}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="wc-dashboard-events-card wc-dashboard-card">
          <h3>Upcoming Events</h3>
          <div className="wc-dashboard-events-list">
            {events.map((e) => (
              <div key={e.id} className="wc-dashboard-event-item" tabIndex={0}>
                <div
                  className="wc-dashboard-event-number"
                  style={{ backgroundColor: e.bgColor }}
                >
                  {e.number}
                </div>
                <div className="wc-dashboard-event-content">
                  <div className="wc-dashboard-event-title">{e.title}</div>
                  <div className="wc-dashboard-event-date">{e.date}</div>
                </div>
                <div className="wc-dashboard-event-fee">{e.fee}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
